import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import NumericInput from '@/components/NumericInput';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import {
  BranchPosProvider,
  FiscalParameters,
  PosDevice,
  posAdapterLabel,
  PosProvider,
  posService
} from '@/data/posService';

/**
 * Optional schema convention a provider can expose via `configSchema` to render
 * a typed credential/settings form instead of raw JSON, e.g.
 * { fields: [{ key: "baseUrl", label: "API URL", target: "settings" }, ...] }
 */
interface PosConfigFieldOption {
  value: string;
  label?: string;
}

interface PosConfigField {
  key: string;
  label?: string;
  type?: 'text' | 'password' | 'number' | 'select';
  target?: 'credentials' | 'settings';
  required?: boolean;
  placeholder?: string;
  options?: PosConfigFieldOption[];
}

const getConfigFields = (
  configSchema?: Record<string, unknown> | null
): PosConfigField[] => {
  const fields = (configSchema as { fields?: unknown } | null | undefined)
    ?.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter(
    (field): field is PosConfigField =>
      !!field && typeof (field as PosConfigField).key === 'string'
  );
};

const groupConfigValues = (
  fields: PosConfigField[],
  values: Record<string, string>
) => {
  const credentials: Record<string, unknown> = {};
  const settings: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[field.key];
    if (raw === undefined || raw === '') continue;
    const value = field.type === 'number' ? Number(raw) : raw;
    if (field.target === 'credentials') credentials[field.key] = value;
    else settings[field.key] = value;
  }
  return {
    credentials: Object.keys(credentials).length ? credentials : undefined,
    settings: Object.keys(settings).length ? settings : undefined
  };
};

/** Token's `tokenBranchId` must be the UUID from the device QR code. */
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Fiscal ÖKC providers (mirrors the backend FISCAL_POS_ADAPTER_KEYS): only they
 * have the `fiscalPaymentEnabled` master switch.
 */
const FISCAL_POS_ADAPTER_KEYS: readonly string[] = ['TOKEN', 'PAVO', 'INPOS'];

const MAX_BASKET_ITEMS_MIN = 1;
const MAX_BASKET_ITEMS_MAX = 1000;

/**
 * The stored cap, but only when the backend would honour it (see
 * resolveTokenMaxBasketItems): a JSON number, integer, 1..1000. Anything else
 * (a numeric string, a decimal, 0, above the cap) is ignored there, so the
 * field must not present it as the active value.
 */
const readMaxBasketItems = (
  settings?: Record<string, unknown> | null
): number | null => {
  const raw = settings?.maxBasketItems;
  return typeof raw === 'number' &&
    Number.isInteger(raw) &&
    raw >= MAX_BASKET_ITEMS_MIN &&
    raw <= MAX_BASKET_ITEMS_MAX
    ? raw
    : null;
};

/** Kuruş (integer minor units) to a localized TL string. */
const formatKurus = (kurus: number): string =>
  (kurus / 100).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });

interface Props {
  branchId: number;
}

const BranchPosSettingsManager = ({ branchId }: Props) => {
  const { t } = useTranslation();
  // A company admin in admin view only monitors a branch: the provider card is
  // read only for them. SUPER_ADMIN keeps full control (CASL: manage all).
  const { isAdminView, isSuperAdmin } = useUserViewMode();
  const readOnly = isAdminView && !isSuperAdmin;
  const [providers, setProviders] = useState<PosProvider[]>([]);
  const [branchProviders, setBranchProviders] = useState<BranchPosProvider[]>(
    []
  );
  const [devices, setDevices] = useState<PosDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Excel Listing T29 — device-reported KDV department list, loaded on
  // demand per device (not every provider supports it; see below).
  const [fiscalParams, setFiscalParams] = useState<
    Record<number, FiscalParameters>
  >({});
  const [loadingFiscalParams, setLoadingFiscalParams] = useState<number | null>(
    null
  );
  // Unsaved maxBasketItems edits per saved branch provider (0 = emptied). The
  // draft is dropped once the PATCH + reload settle so the field never snaps
  // back to the old stored value mid save.
  const [maxBasketDrafts, setMaxBasketDrafts] = useState<
    Record<number, number>
  >({});

  // Provider cards being saved. Every settings write sends the WHOLE settings
  // object built from the card's render time snapshot and the backend replaces
  // it wholesale, so a second change made before the first save AND the reload
  // settled would silently undo the first one. The card's write controls stay
  // disabled until both are done (the ref also blocks a re-entrant call that
  // arrives before React re-rendered the disabled state).
  const [savingProviders, setSavingProviders] = useState<
    Record<number, boolean>
  >({});
  const savingProvidersRef = useRef<Set<number>>(new Set());

  // Pavo pairing modal — one at a time. `deviceId` acts as the "open" flag.
  // Poll state lives here so the async `startDevicePairing` → `checkDevicePairing`
  // loop is cancelable when the dialog closes.
  const [pairingDeviceId, setPairingDeviceId] = useState<number | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingApproved, setPairingApproved] = useState(false);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [pairingBusy, setPairingBusy] = useState(false);

  // InPOS matchId auto-discover: debounced call to /pos-inpos/discover-matches
  // whenever VKN in the form changes. Result drives whether the matchId input
  // is a plain text box, an auto-filled read-only value (single match), or a
  // dropdown (multiple matches).
  const [inposMatches, setInposMatches] = useState<Array<{
    matchId: string;
    name?: string;
  }> | null>(null);
  const [inposDiscovering, setInposDiscovering] = useState(false);
  const [inposDiscoverError, setInposDiscoverError] = useState<string | null>(
    null
  );
  const [copiedFlag, setCopiedFlag] = useState(false);

  const [providerForm, setProviderForm] = useState({
    providerId: '',
    isDefault: true,
    credentialsJson: '',
    settingsJson: '{\n  "mockScenario": "success"\n}'
  });
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [deviceForm, setDeviceForm] = useState({
    branchPosProviderId: '',
    label: '',
    terminalId: '',
    isDefault: true
  });

  const selectedProvider = useMemo(
    () =>
      providers.find(
        (provider) => provider.id === Number(providerForm.providerId)
      ),
    [providers, providerForm.providerId]
  );

  // Public URL the InPOS TMS portal will POST completed orders to. Derived
  // from the same backend base URL the admin's own API client uses (so ngrok
  // in dev / prod host in prod both work without extra config).
  const inposCallbackUrl = useMemo(
    () =>
      `${(import.meta.env.VITE_BACKEND_URL ?? '').replace(/\/$/, '')}/pos/webhooks/inpos`,
    []
  );

  const configFields = useMemo(() => {
    const fields = getConfigFields(selectedProvider?.configSchema);
    // Token/Beko's OAuth client (clientId/clientSecret) is Posanto's single
    // integrator credential, read from the backend environment. A branch admin
    // is never asked for it, even if an older provider row still lists it.
    return selectedProvider?.adapterKey === 'TOKEN'
      ? fields.filter((field) => field.target !== 'credentials')
      : fields;
  }, [selectedProvider]);
  const useDynamicForm = configFields.length > 0;

  // The DB stores `label` in English; the frontend resolves a per-provider
  // i18n key when available so a Turkish operator sees Turkish labels.
  const fieldLabel = (field: PosConfigField): string => {
    if (!selectedProvider) return field.label ?? field.key;
    const key = `pos.field.${selectedProvider.code.toLowerCase()}.${field.key}`;
    return t(key, { defaultValue: field.label ?? field.key });
  };

  const fieldOptionLabel = (
    field: PosConfigField,
    option: PosConfigFieldOption
  ): string => {
    if (!selectedProvider) return option.label ?? option.value;
    const key = `pos.field.${selectedProvider.code.toLowerCase()}.${field.key}.${option.value}`;
    return t(key, { defaultValue: option.label ?? option.value });
  };

  // Two-tier helper text lookup for a dynamic config field:
  //  1) provider+field-specific hint (`pos.field.<provider>.<key>.help`)
  //     e.g. Token's tokenBranchId → "scan the device QR menu"
  //  2) fallback: encrypted-storage note for credentials
  const fieldHelperText = (field: PosConfigField): string | undefined => {
    if (selectedProvider) {
      const helpKey = `pos.field.${selectedProvider.code.toLowerCase()}.${field.key}.help`;
      const resolved = t(helpKey, { defaultValue: '' });
      if (resolved) return resolved;
    }
    return field.target === 'credentials'
      ? t('pos.settings.encryptedStorage')
      : undefined;
  };

  const parseJson = (value: string, label: string) => {
    if (!value.trim()) return undefined;
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      throw new Error(t('pos.settings.invalidJson', { label }));
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [providerRows, branchProviderRows, deviceRows] = await Promise.all([
        posService.listProviders(),
        posService.listBranchProviders(branchId),
        posService.listDevices(branchId)
      ]);
      setProviders(providerRows);
      setBranchProviders(branchProviderRows);
      setDevices(deviceRows);
      if (!providerForm.providerId && providerRows[0]) {
        setProviderForm((current) => ({
          ...current,
          providerId: String(providerRows[0].id)
        }));
      }
      if (!deviceForm.branchPosProviderId && branchProviderRows[0]) {
        setDeviceForm((current) => ({
          ...current,
          branchPosProviderId: String(branchProviderRows[0].id)
        }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('pos.settings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  /** Runs one settings write of a provider card (PATCH + reload) exclusively. */
  const runProviderSave = async (
    providerId: number,
    save: () => Promise<void>
  ) => {
    if (savingProvidersRef.current.has(providerId)) return;
    savingProvidersRef.current.add(providerId);
    setSavingProviders((current) => ({ ...current, [providerId]: true }));
    try {
      await save();
    } finally {
      savingProvidersRef.current.delete(providerId);
      setSavingProviders((current) => {
        const rest = { ...current };
        delete rest[providerId];
        return rest;
      });
    }
  };

  const createBranchProvider = async () => {
    setError(null);
    setMessage(null);
    try {
      const missing = useDynamicForm
        ? configFields.find((f) => f.required && !fieldValues[f.key]?.trim())
        : undefined;
      if (missing) {
        throw new Error(
          t('pos.settings.fieldRequired', { field: fieldLabel(missing) })
        );
      }

      const { credentials, settings } = useDynamicForm
        ? groupConfigValues(configFields, fieldValues)
        : {
            credentials: parseJson(
              providerForm.credentialsJson,
              t('pos.settings.credentialsJson')
            ),
            settings: parseJson(
              providerForm.settingsJson,
              t('pos.settings.settingsJson')
            )
          };

      await posService.createBranchProvider(branchId, {
        providerId: Number(providerForm.providerId),
        // displayName omitted — backend defaults it to the provider's catalog
        // name (e.g. "Pavo"). Branches almost never override this in practice.
        isDefault: providerForm.isDefault,
        credentials,
        settings
      });
      setMessage(t('pos.settings.providerSaved'));
      setFieldValues({});
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err.message ??
          t('pos.settings.saveError')
      );
    }
  };

  const createDevice = async () => {
    setError(null);
    setMessage(null);
    try {
      const createdDevice = await posService.createDevice(branchId, {
        branchPosProviderId: Number(deviceForm.branchPosProviderId),
        label: deviceForm.label,
        terminalId: deviceForm.terminalId || undefined,
        // serialNumber omitted — for every provider the device identifier is
        // the same value operators read off the terminal (Pavo PAV..., Token
        // AV.../AT..., InPOS device CSN). Kept as one field: terminalId.
        // connectionType is inherited from the branch's provider on the backend
        // (every provider we support is CLOUD_API) — no dropdown for it.
        // metadata not sent from the UI: no provider needs raw JSON from a
        // branch admin — Token's `tokenBranchId` is now a typed provider
        // setting (see configSchema).
        isDefault: deviceForm.isDefault
      });
      setMessage(t('pos.settings.deviceSaved'));
      await load();

      // Pavo needs a one-time interactive pairing right after the device
      // exists (cashier approves a code on the physical terminal). The
      // separate "Cihazı Eşleştir" button on the device row still works as
      // a fallback / re-pair path, but the operator should never have to
      // hunt for it on first setup — auto-open the modal the moment the
      // device is created. No-op for Token/InPOS (their auto-register is
      // silent and needs no interactive step).
      const savedDeviceBranchProvider = branchProviders.find(
        (bp) => bp.id === createdDevice.branchPosProviderId
      );
      if (savedDeviceBranchProvider?.provider.adapterKey === 'PAVO') {
        openPairingDialog(createdDevice.id);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err.message ??
          t('pos.settings.deviceSaveError')
      );
    }
  };

  // `refresh` bypasses the backend's 24h cache; only used when a table is
  // already on screen (first load stays a plain cached call).
  const loadFiscalParameters = async (deviceId: number, refresh: boolean) => {
    setLoadingFiscalParams(deviceId);
    setError(null);
    try {
      const result = refresh
        ? await posService.getFiscalParameters(branchId, deviceId, {
            refresh: true
          })
        : await posService.getFiscalParameters(branchId, deviceId);
      setFiscalParams((current) => ({
        ...current,
        [deviceId]: { ...result, sections: result.sections ?? [] }
      }));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? t('pos.settings.fiscalParametersError')
      );
    } finally {
      setLoadingFiscalParams(null);
    }
  };

  const changeBasketMode = async (
    provider: BranchPosProvider,
    nextMode: string
  ) => {
    setMessage(null);
    if (nextMode === 'deferred') {
      // Deferred mode talks to the device with the QR branch UUID; a terminal
      // id (AV... / AT...) there is rejected by the backend, so stop early
      // and leave the select on its previous value.
      const tokenBranchId = provider.settings?.tokenBranchId;
      if (
        typeof tokenBranchId !== 'string' ||
        !UUID_REGEX.test(tokenBranchId)
      ) {
        setError(t('pos.settings.tokenBranchIdInvalidForDeferred'));
        return;
      }
    }
    setError(null);
    await runProviderSave(provider.id, async () => {
      try {
        await posService.updateBranchProvider(branchId, provider.id, {
          settings: {
            ...(provider.settings ?? {}),
            basketMode: nextMode
          }
        });
        await load();
      } catch (err: any) {
        setError(
          err?.response?.data?.message ??
            err?.message ??
            t('pos.settings.saveError')
        );
      }
    });
  };

  const saveMaxBasketItems = async (
    provider: BranchPosProvider,
    rawValue: string
  ) => {
    const trimmed = rawValue.trim();
    let next: number | null = null;
    if (trimmed !== '') {
      next = Number(trimmed);
      if (
        !Number.isInteger(next) ||
        next < MAX_BASKET_ITEMS_MIN ||
        next > MAX_BASKET_ITEMS_MAX
      ) {
        setMessage(null);
        setError(t('pos.settings.maxBasketItemsInvalid'));
        return;
      }
    }
    const dropDraft = () =>
      setMaxBasketDrafts((current) => {
        const rest = { ...current };
        delete rest[provider.id];
        return rest;
      });
    if (next === readMaxBasketItems(provider.settings)) {
      dropDraft();
      return;
    }
    setError(null);
    setMessage(null);
    // `settings` replaces the stored object wholesale: merge, and DROP the key
    // (not null) when emptied so the backend default (150) applies.
    const settings: Record<string, unknown> = { ...(provider.settings ?? {}) };
    if (next === null) delete settings.maxBasketItems;
    else settings.maxBasketItems = next;
    await runProviderSave(provider.id, async () => {
      try {
        await posService.updateBranchProvider(branchId, provider.id, {
          settings
        });
        setMessage(t('pos.settings.providerSaved'));
        await load();
      } catch (err: any) {
        setError(
          err?.response?.data?.message ??
            err?.message ??
            t('pos.settings.saveError')
        );
      } finally {
        dropDraft();
      }
    });
  };

  // Pavo pairing state machine — cancel-safe. The ref is checked between the
  // network-return and the setState so a dialog closed mid-poll never scribbles
  // stale approval state back over the fresh (closed) UI.
  const pairingSessionRef = useRef<{
    deviceId: number;
    pairingId: string;
  } | null>(null);

  const closePairingDialog = useCallback(() => {
    pairingSessionRef.current = null;
    setPairingDeviceId(null);
    setPairingCode(null);
    setPairingApproved(false);
    setPairingError(null);
    setPairingBusy(false);
  }, []);

  const openPairingDialog = useCallback(
    async (deviceId: number) => {
      setPairingDeviceId(deviceId);
      setPairingCode(null);
      setPairingApproved(false);
      setPairingError(null);
      setPairingBusy(true);
      try {
        const result = await posService.startDevicePairing(branchId, deviceId);
        if (!result.pairingId) {
          setPairingError(t('pos.settings.pairingFailed'));
          return;
        }
        pairingSessionRef.current = {
          deviceId,
          pairingId: result.pairingId
        };
        setPairingCode(result.pairingCode ?? null);
        setPairingApproved(!!result.isApproved);
      } catch (err: any) {
        setPairingError(
          err?.response?.data?.message ?? t('pos.settings.pairingFailed')
        );
      } finally {
        setPairingBusy(false);
      }
    },
    [branchId, t]
  );

  // InPOS-only side effect: when the currently-selected provider is InPOS AND
  // the user has typed something into the `vkn` field, kick off a debounced
  // discoverInposMatches call. Result populates the matchId field automatically
  // (single match) or a dropdown of options (multiple). Never blocks the form.
  useEffect(() => {
    if (selectedProvider?.adapterKey !== 'INPOS') {
      setInposMatches(null);
      setInposDiscoverError(null);
      return;
    }
    const vkn = (fieldValues.vkn ?? '').trim();
    if (!/^\d{10}$/.test(vkn)) {
      setInposMatches(null);
      setInposDiscoverError(null);
      return;
    }
    setInposDiscovering(true);
    setInposDiscoverError(null);
    const handle = window.setTimeout(async () => {
      try {
        const result = await posService.discoverInposMatches(branchId, vkn);
        setInposMatches(result.matches);
        if (result.matches.length === 1) {
          // Auto-fill matchId — single unambiguous match. Overwrite whatever's
          // there since the operator typed a VKN specifically to resolve this.
          setFieldValues((current) => ({
            ...current,
            matchId: result.matches[0].matchId
          }));
        }
      } catch (err: any) {
        setInposDiscoverError(
          err?.response?.data?.message ?? t('pos.settings.inposMatchNone')
        );
        setInposMatches(null);
      } finally {
        setInposDiscovering(false);
      }
    }, 500);
    return () => {
      window.clearTimeout(handle);
    };
  }, [selectedProvider, fieldValues.vkn, branchId, t]);

  // Poll checkDevicePairing every 2s while the dialog is open, has a
  // pairingId, and is not yet approved. The polling stops as soon as the
  // dialog closes, an error appears, or approval lands.
  //
  // `pairingCode` in the dep list is deliberate — pairingSessionRef.current
  // is set asynchronously AFTER startDevicePairing() resolves, so effects
  // keyed only on pairingDeviceId would fire once (with a null ref) and
  // never again. Since we `setPairingCode(...)` from the same code branch
  // that sets the ref, that state transition is our signal that the ref is
  // now valid and polling should start.
  useEffect(() => {
    if (pairingDeviceId == null) return;
    if (pairingApproved) return;
    if (pairingError) return;
    if (!pairingSessionRef.current) return;
    const session = pairingSessionRef.current;
    const check = async () => {
      try {
        const result = await posService.checkDevicePairing(
          branchId,
          session.deviceId,
          session.pairingId
        );
        if (pairingSessionRef.current !== session) return;
        if (result.isApproved) {
          setPairingApproved(true);
          // Refresh device list so the underlying row's metadata.pairing
          // reflects reality (hides the "Pair device" button after close).
          try {
            const deviceRows = await posService.listDevices(branchId);
            setDevices(deviceRows);
          } catch {
            // best-effort; the user can always reload the page manually
          }
        }
      } catch (err: any) {
        if (pairingSessionRef.current !== session) return;
        setPairingError(
          err?.response?.data?.message ?? t('pos.settings.pairingFailed')
        );
      }
    };
    // Kick off an immediate check so the user doesn't wait a full interval
    // after approving on the device.
    check();
    const timer = window.setInterval(check, 2000);
    return () => {
      window.clearInterval(timer);
    };
  }, [
    pairingDeviceId,
    pairingCode,
    pairingApproved,
    pairingError,
    branchId,
    t
  ]);

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h5" color="text.primary" fontWeight="bold">
            {t('pos.settings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t('pos.settings.description')}
          </Typography>
        </Box>
        {loading && <CircularProgress size={24} />}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={branchProviders.length > 0 ? 6 : 12}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('pos.settings.addProvider')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              label={t('pos.settings.provider')}
              value={providerForm.providerId}
              onChange={(event) =>
                setProviderForm((current) => ({
                  ...current,
                  providerId: event.target.value
                }))
              }
              fullWidth
            >
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name} ({posAdapterLabel(provider.adapterKey)})
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={providerForm.isDefault}
                  onChange={(event) =>
                    setProviderForm((current) => ({
                      ...current,
                      isDefault: event.target.checked
                    }))
                  }
                />
              }
              label={t('pos.settings.defaultProvider')}
            />
            {useDynamicForm ? (
              configFields.map((field) => {
                // InPOS matchId: when the VKN-driven discover call returned
                // more than one match, render this field as a dropdown of the
                // returned matchIds instead of a free-text box. Single-match
                // case is handled by the effect: matchId is auto-filled and
                // the plain TextField below shows it (still editable, so an
                // operator can override).
                const isInposMultiMatch =
                  selectedProvider?.adapterKey === 'INPOS' &&
                  field.key === 'matchId' &&
                  inposMatches &&
                  inposMatches.length > 1;
                if (isInposMultiMatch) {
                  return (
                    <TextField
                      select
                      key={field.key}
                      label={`${fieldLabel(field)}${field.required ? ' *' : ''}`}
                      value={fieldValues[field.key] ?? ''}
                      onChange={(event) =>
                        setFieldValues((current) => ({
                          ...current,
                          [field.key]: event.target.value
                        }))
                      }
                      helperText={t('pos.settings.inposMatchPick')}
                      fullWidth
                    >
                      {inposMatches!.map((m) => (
                        <MenuItem key={m.matchId} value={m.matchId}>
                          {m.name ? `${m.name} — ${m.matchId}` : m.matchId}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                if (field.type === 'number') {
                  // Integer-only NumericInput; 0 / blank both mean "not set"
                  // and are omitted by groupConfigValues.
                  const rawValue = fieldValues[field.key];
                  const bounds =
                    field.key === 'maxBasketItems'
                      ? { min: MAX_BASKET_ITEMS_MIN, max: MAX_BASKET_ITEMS_MAX }
                      : {};
                  return (
                    <NumericInput
                      key={field.key}
                      label={`${fieldLabel(field)}${field.required ? ' *' : ''}`}
                      value={rawValue ? Number(rawValue) : 0}
                      onChange={(next) =>
                        setFieldValues((current) => ({
                          ...current,
                          [field.key]: next === 0 ? '' : String(next)
                        }))
                      }
                      allowDecimals={false}
                      {...bounds}
                      placeholder={field.placeholder}
                      helperText={fieldHelperText(field)}
                      fullWidth
                    />
                  );
                }
                return field.type === 'select' ? (
                  <TextField
                    select
                    key={field.key}
                    label={`${fieldLabel(field)}${field.required ? ' *' : ''}`}
                    value={fieldValues[field.key] ?? ''}
                    onChange={(event) =>
                      setFieldValues((current) => ({
                        ...current,
                        [field.key]: event.target.value
                      }))
                    }
                    helperText={fieldHelperText(field)}
                    fullWidth
                  >
                    {!field.required && (
                      <MenuItem value="">
                        <em>{t('select')}</em>
                      </MenuItem>
                    )}
                    {(field.options ?? []).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {fieldOptionLabel(field, option)}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    key={field.key}
                    label={`${fieldLabel(field)}${field.required ? ' *' : ''}`}
                    type={field.type === 'password' ? 'password' : 'text'}
                    value={fieldValues[field.key] ?? ''}
                    onChange={(event) =>
                      setFieldValues((current) => ({
                        ...current,
                        [field.key]: event.target.value
                      }))
                    }
                    placeholder={field.placeholder}
                    helperText={fieldHelperText(field)}
                    fullWidth
                  />
                );
              })
            ) : (
              <>
                <TextField
                  label={t('pos.settings.credentialsJson')}
                  value={providerForm.credentialsJson}
                  onChange={(event) =>
                    setProviderForm((current) => ({
                      ...current,
                      credentialsJson: event.target.value
                    }))
                  }
                  minRows={4}
                  multiline
                  fullWidth
                  placeholder='{"merchantId":"...","apiKey":"..."}'
                />
                <TextField
                  label={t('pos.settings.settingsJson')}
                  value={providerForm.settingsJson}
                  onChange={(event) =>
                    setProviderForm((current) => ({
                      ...current,
                      settingsJson: event.target.value
                    }))
                  }
                  minRows={4}
                  multiline
                  fullWidth
                />
              </>
            )}
            {/* InPOS-only inline discovery status + webhook portal panel.
                Rendered in the create-form column so the operator sees it in
                one place while setting up. */}
            {selectedProvider?.adapterKey === 'INPOS' && (
              <>
                {inposDiscovering && (
                  <Alert severity="info">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={14} />
                      {t('pos.settings.inposDiscovering')}
                    </Box>
                  </Alert>
                )}
                {!inposDiscovering &&
                  inposMatches &&
                  inposMatches.length === 1 && (
                    <Alert severity="success">
                      {t('pos.settings.inposMatchAuto')}
                    </Alert>
                  )}
                {!inposDiscovering &&
                  inposMatches &&
                  inposMatches.length === 0 && (
                    <Alert severity="warning">
                      {t('pos.settings.inposMatchNone')}
                    </Alert>
                  )}
                {!inposDiscovering && inposDiscoverError && (
                  <Alert severity="error">{inposDiscoverError}</Alert>
                )}

                <Alert severity="info" icon={false}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('pos.settings.inposWebhookPanelTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('pos.settings.inposWebhookPanelBody')}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <TextField
                      value={inposCallbackUrl}
                      InputProps={{ readOnly: true }}
                      size="small"
                      fullWidth
                      sx={{ fontFamily: 'monospace' }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(inposCallbackUrl);
                          setCopiedFlag(true);
                          window.setTimeout(() => setCopiedFlag(false), 2000);
                        } catch {
                          /* clipboard blocked — user can still copy manually */
                        }
                      }}
                    >
                      {copiedFlag
                        ? t('pos.settings.copied')
                        : t('pos.settings.copy')}
                    </Button>
                  </Box>
                </Alert>
              </>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={createBranchProvider}
                disabled={!providerForm.providerId}
              >
                {t('pos.settings.saveProvider')}
              </Button>
            </Box>
          </Stack>
        </Grid>

        {branchProviders.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('pos.settings.addDevice')}
            </Typography>
            <Stack spacing={2}>
              <TextField
                select
                label={t('pos.settings.branchProvider')}
                value={deviceForm.branchPosProviderId}
                onChange={(event) =>
                  setDeviceForm((current) => ({
                    ...current,
                    branchPosProviderId: event.target.value
                  }))
                }
                fullWidth
              >
                {branchProviders.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.displayName} (
                    {posAdapterLabel(provider.provider.adapterKey)})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('pos.settings.deviceName')}
                value={deviceForm.label}
                onChange={(event) =>
                  setDeviceForm((current) => ({
                    ...current,
                    label: event.target.value
                  }))
                }
                fullWidth
              />
              <TextField
                label={t('pos.settings.terminalId')}
                value={deviceForm.terminalId}
                onChange={(event) =>
                  setDeviceForm((current) => ({
                    ...current,
                    terminalId: event.target.value
                  }))
                }
                placeholder="PAV960000079 · AV00000000001 · m530 CSN"
                fullWidth
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={deviceForm.isDefault}
                    onChange={(event) =>
                      setDeviceForm((current) => ({
                        ...current,
                        isDefault: event.target.checked
                      }))
                    }
                  />
                }
                label={t('pos.settings.defaultDevice')}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={createDevice}
                  disabled={
                    !deviceForm.branchPosProviderId || !deviceForm.label
                  }
                >
                  {t('pos.settings.saveDevice')}
                </Button>
              </Box>
            </Stack>
          </Grid>
        )}
      </Grid>

      {branchProviders.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('pos.settings.definedProviders')}
          </Typography>
          <Stack spacing={1.5} mb={devices.length > 0 ? 3 : 0}>
            {branchProviders.map((provider) => {
              const providerSaving = Boolean(savingProviders[provider.id]);
              return (
                <Box
                  key={provider.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <Box>
                      <Typography fontWeight="bold">
                        {provider.displayName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {provider.provider.name} /{' '}
                        {posAdapterLabel(provider.provider.adapterKey)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {provider.isDefault && (
                        <Chip label={t('pos.settings.default')} size="small" />
                      )}
                      <Chip
                        label={
                          provider.isActive
                            ? t('pos.settings.active')
                            : t('pos.settings.passive')
                        }
                        color={provider.isActive ? 'success' : 'default'}
                        size="small"
                      />
                      {!readOnly && (
                        <Button
                          color="error"
                          size="small"
                          onClick={async () => {
                            await posService.deleteBranchProvider(
                              branchId,
                              provider.id
                            );
                            await load();
                          }}
                        >
                          {t('pos.settings.delete')}
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {/* The master switch of a fiscal ÖKC: while it is off the
                    backend sends no sale to the device, card included. A plain
                    card terminal (Ingenico) has no such switch. */}
                  {FISCAL_POS_ADAPTER_KEYS.includes(
                    provider.provider.adapterKey
                  ) && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(
                              provider.settings?.fiscalPaymentEnabled
                            )}
                            disabled={readOnly || providerSaving}
                            onChange={async (event) => {
                              const checked = event.target.checked;
                              setError(null);
                              await runProviderSave(provider.id, async () => {
                                try {
                                  await posService.updateBranchProvider(
                                    branchId,
                                    provider.id,
                                    {
                                      settings: {
                                        ...(provider.settings ?? {}),
                                        fiscalPaymentEnabled: checked
                                      }
                                    }
                                  );
                                  await load();
                                } catch (err: any) {
                                  setError(
                                    err?.response?.data?.message ??
                                      err?.message ??
                                      t('pos.settings.saveError')
                                  );
                                }
                              });
                            }}
                          />
                        }
                        label={t('pos.settings.fiscalPaymentEnabled')}
                        slotProps={{ typography: { variant: 'body2' } }}
                        sx={{ m: 0 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {t('pos.settings.fiscalPaymentEnabledHelp')}
                      </Typography>
                    </Box>
                  )}

                  {/* Token's webhook registration (Set Client Settings) is
                    deliberately INVISIBLE here. The backend registers it by
                    itself at boot, once a day and on relevant provider saves
                    (pos.service.ts ensureTokenWebhooks) and never throws to
                    the caller; the outcome is persisted on the provider's
                    settings JSON (clientSettingsRegistered / clientSettingsError
                    / clientSettingsVerified*, server owned, kept on every
                    save) and logged, for ops to read.
                    A branch admin cannot fix a callback base URL or a rotated
                    secret — both are deployment env — so showing them a
                    warning and a retry button only produced alarm with no
                    remedy. The daily check is the recovery path; the manual endpoint
                    POST .../pos-devices/:deviceId/token/register-webhook stays
                    available for ops. Do NOT re-add a UI surface for this. */}

                  {/* Listing Mode (E8) — Token/Beko only; meaningless for other adapters. */}
                  {provider.provider.adapterKey === 'TOKEN' && (
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1.5 }}
                    >
                      <TextField
                        select
                        size="small"
                        label={t('pos.settings.basketMode')}
                        value={
                          provider.settings?.basketMode === 'deferred'
                            ? 'deferred'
                            : 'instant'
                        }
                        onChange={(event) =>
                          changeBasketMode(provider, event.target.value)
                        }
                        disabled={readOnly || providerSaving}
                        sx={{ maxWidth: 320 }}
                        fullWidth
                      >
                        <MenuItem value="instant">
                          {t('pos.settings.basketModeInstant')}
                        </MenuItem>
                        <MenuItem value="deferred">
                          {t('pos.settings.basketModeDeferred')}
                        </MenuItem>
                      </TextField>
                      <NumericInput
                        size="small"
                        label={t('pos.field.token.maxBasketItems')}
                        placeholder={t(
                          'pos.settings.maxBasketItemsPlaceholder'
                        )}
                        helperText={t('pos.field.token.maxBasketItems.help')}
                        value={
                          maxBasketDrafts[provider.id] ??
                          readMaxBasketItems(provider.settings) ??
                          0
                        }
                        onChange={(next) =>
                          setMaxBasketDrafts((current) => ({
                            ...current,
                            [provider.id]: next
                          }))
                        }
                        onBlur={(event) =>
                          saveMaxBasketItems(provider, event.target.value)
                        }
                        allowDecimals={false}
                        min={MAX_BASKET_ITEMS_MIN}
                        max={MAX_BASKET_ITEMS_MAX}
                        disabled={readOnly || providerSaving}
                        sx={{ maxWidth: 320 }}
                        fullWidth
                      />
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>

          {devices.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('pos.settings.definedDevices')}
              </Typography>
              <Stack spacing={1}>
                {devices.map((device) => {
                  // Only Token/Beko implements getFiscalParameters — InPOS and
                  // Pavo have no device-side KDV department/section listing
                  // endpoint, so pos.service.ts rejects them with a fixed
                  // "not supported" error. Hide the button instead of letting
                  // the operator hit that error on every click.
                  const deviceAdapterKey = branchProviders.find(
                    (bp) => bp.id === device.branchPosProviderId
                  )?.provider.adapterKey;
                  const supportsFiscalParameters = deviceAdapterKey === 'TOKEN';
                  // Pavo cihazi elde onaylamayan bir eslesme adimi ister
                  // (backend `POST .../pair`, kod cihazda çikar, cashier onayla
                  // basar). Adapter yalnizca PAVO icin pairDevice implement
                  // eder; Token/InPOS bulut tarafinda hallederler.
                  const supportsPairing = deviceAdapterKey === 'PAVO';
                  // Cihaz metadata'sinda son eslesmenin isApproved: true olup
                  // olmadigi. Yoksa buton "Eslestir", varsa "Yeniden eslestir"
                  // gorunumunde.
                  const pairing = (
                    device.metadata as
                      | { pairing?: { isApproved?: boolean } }
                      | null
                      | undefined
                  )?.pairing;
                  const isPaired = pairing?.isApproved === true;
                  const deviceParams = fiscalParams[device.id];

                  return (
                    <Box key={device.id}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Box>
                          <Typography fontWeight="bold">
                            {device.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('pos.settings.terminalId')}:{' '}
                            {device.terminalId || '-'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          {device.isDefault && (
                            <Chip
                              label={t('pos.settings.default')}
                              size="small"
                            />
                          )}
                          <Chip
                            label={
                              device.isActive
                                ? t('pos.settings.active')
                                : t('pos.settings.passive')
                            }
                            color={device.isActive ? 'success' : 'default'}
                            size="small"
                          />
                          {supportsPairing && (
                            <Button
                              size="small"
                              variant={isPaired ? 'text' : 'contained'}
                              color={isPaired ? 'inherit' : 'primary'}
                              onClick={() => openPairingDialog(device.id)}
                            >
                              {isPaired
                                ? t('pos.settings.pairAgain')
                                : t('pos.settings.pair')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            onClick={async () => {
                              setMessage(null);
                              setError(null);
                              try {
                                const result = await posService.healthCheck(
                                  branchId,
                                  device.id
                                );
                                if (result.healthy) {
                                  setMessage(t('pos.settings.deviceHealthy'));
                                } else {
                                  setError(
                                    result.errorMessage ||
                                      t('pos.settings.deviceUnhealthy')
                                  );
                                }
                              } catch (err: any) {
                                setError(
                                  err?.response?.data?.message ??
                                    err?.message ??
                                    t('pos.settings.deviceUnhealthy')
                                );
                              }
                            }}
                          >
                            {t('pos.settings.health')}
                          </Button>
                          {supportsFiscalParameters && (
                            <Button
                              size="small"
                              disabled={loadingFiscalParams === device.id}
                              onClick={() =>
                                loadFiscalParameters(
                                  device.id,
                                  Boolean(deviceParams)
                                )
                              }
                            >
                              {loadingFiscalParams === device.id ? (
                                <CircularProgress size={16} />
                              ) : deviceParams ? (
                                t('pos.settings.fiscalParametersRefresh')
                              ) : (
                                t('pos.settings.fiscalParametersLoad')
                              )}
                            </Button>
                          )}
                          <Button
                            color="error"
                            size="small"
                            onClick={async () => {
                              await posService.deleteDevice(
                                branchId,
                                device.id
                              );
                              await load();
                            }}
                          >
                            {t('pos.settings.delete')}
                          </Button>
                        </Stack>
                      </Box>

                      {supportsFiscalParameters && deviceParams && (
                        <Box mt={1.5} mb={1}>
                          <Stack
                            direction="row"
                            spacing={3}
                            flexWrap="wrap"
                            useFlexGap
                            mb={1.5}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {t('pos.settings.fiscalParametersReceiptLimit')}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {typeof deviceParams.receiptLimit === 'number'
                                  ? formatKurus(deviceParams.receiptLimit)
                                  : t('pos.settings.fiscalParametersUnknown')}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {t('pos.settings.fiscalParametersEDocument')}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {typeof deviceParams.eDocumentStatus !==
                                'number'
                                  ? t('pos.settings.fiscalParametersUnknown')
                                  : deviceParams.eDocumentStatus === 1
                                    ? t(
                                        'pos.settings.fiscalParametersEDocumentActive'
                                      )
                                    : t(
                                        'pos.settings.fiscalParametersEDocumentPassive'
                                      )}
                              </Typography>
                            </Box>
                            {typeof deviceParams.integratorID === 'number' && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {t('pos.settings.fiscalParametersIntegrator')}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {deviceParams.integratorID}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                          {deviceParams.sections.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('pos.settings.fiscalParametersEmpty')}
                            </Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    {t(
                                      'pos.settings.fiscalParametersSectionNo'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {t(
                                      'pos.settings.fiscalParametersTaxPercent'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {t('pos.settings.fiscalParametersName')}
                                  </TableCell>
                                  <TableCell>
                                    {t(
                                      'pos.settings.fiscalParametersSectionLimit'
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {deviceParams.sections.map((section) => (
                                  <TableRow key={section.sectionNo}>
                                    <TableCell>{section.sectionNo}</TableCell>
                                    <TableCell>
                                      %{(section.taxPercent / 100).toFixed(2)}
                                    </TableCell>
                                    <TableCell>{section.name ?? '-'}</TableCell>
                                    <TableCell>
                                      {section.limit
                                        ? formatKurus(section.limit)
                                        : t(
                                            'pos.settings.fiscalParametersNoLimit'
                                          )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </>
      )}

      <Dialog
        open={pairingDeviceId != null}
        onClose={pairingBusy ? undefined : closePairingDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('pos.settings.pairDialogTitle')}</DialogTitle>
        <DialogContent>
          {pairingError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {pairingError}
            </Alert>
          ) : pairingApproved ? (
            <Alert severity="success" sx={{ mb: 1 }}>
              {t('pos.settings.pairSuccess')}
            </Alert>
          ) : pairingCode ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('pos.settings.pairInstructions')}
              </Typography>
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  borderRadius: 1
                }}
              >
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  letterSpacing={4}
                  sx={{ fontFamily: 'monospace' }}
                >
                  {pairingCode}
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                mt={2}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('pos.settings.pairWaiting')}
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={4}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePairingDialog} disabled={pairingBusy}>
            {pairingApproved || pairingError
              ? t('pos.settings.close')
              : t('pos.settings.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchPosSettingsManager;
